import type {
  ConversationId,
  FileId,
  McpServerName,
  MessageId,
  ScheduledTaskId,
  TenantId,
} from '../ids.ts';

export const API_VERSION = 'v1';

export const SCHEDULED_TASK_ACTION_NAMES = ['confirm', 'dismiss', 'pause', 'resume'] as const;
export type ScheduledTaskAction = (typeof SCHEDULED_TASK_ACTION_NAMES)[number];

export const API_ROUTES = {
  healthz: '/healthz',
  readyz: '/readyz',
  frame: '/v1/frame',
  sessions: '/v1/sessions',
  currentSession: '/v1/sessions/current',
  chat: '/v1/chat',
  chatStream: (id: ConversationId) => `/v1/chat/${id}/stream`,
  chatStop: (id: ConversationId) => `/v1/chat/${id}/stop`,
  conversations: '/v1/conversations',
  conversation: (id: ConversationId) => `/v1/conversations/${id}`,
  conversationRead: (id: ConversationId) => `/v1/conversations/${id}/read`,
  unreadCount: '/v1/unread',
  scheduledTasks: '/v1/scheduled-tasks',
  scheduledTask: (id: ScheduledTaskId) => `/v1/scheduled-tasks/${id}`,
  scheduledTaskAction: (id: ScheduledTaskId, action: ScheduledTaskAction) =>
    `/v1/scheduled-tasks/${id}/${action}`,
  messageFeedback: (id: ConversationId, messageId: MessageId) =>
    `/v1/conversations/${id}/messages/${messageId}/feedback`,
  file: (id: FileId) => `/v1/files/${id}`,
  fileContent: (id: FileId) => `/v1/files/${id}/content`,
  connections: '/v1/connections',
  connection: (server: McpServerName) => `/v1/connections/${server}`,
  connectionKey: (server: McpServerName) => `/v1/connections/${server}/key`,
  connectionSignIn: (server: McpServerName) => `/v1/connections/${server}/sign-in`,
  signInStart: '/v1/sign-in/start',
  signInBegin: '/v1/sign-in/begin',
  signInCallback: '/v1/sign-in/callback',
  signInClient: (tenant: TenantId) => `/v1/sign-in/clients/${tenant}`,
  skills: '/v1/skills',
  skillMarketplace: '/v1/skills/marketplace',
  skill: (id: string) => `/v1/skills/${id}`,
} as const;

export const API_ROUTE_PATTERNS = {
  healthz: '/healthz',
  readyz: '/readyz',
  frame: '/v1/frame',
  sessions: '/v1/sessions',
  currentSession: '/v1/sessions/current',
  chat: '/v1/chat',
  chatStream: '/v1/chat/:id/stream',
  chatStop: '/v1/chat/:id/stop',
  conversations: '/v1/conversations',
  conversation: '/v1/conversations/:id',
  conversationRead: '/v1/conversations/:id/read',
  unreadCount: '/v1/unread',
  scheduledTasks: '/v1/scheduled-tasks',
  scheduledTask: '/v1/scheduled-tasks/:id',
  scheduledTaskAction: '/v1/scheduled-tasks/:id/:action',
  messageFeedback: '/v1/conversations/:id/messages/:messageId/feedback',
  file: '/v1/files/:id',
  fileContent: '/v1/files/:id/content',
  connections: '/v1/connections',
  connection: '/v1/connections/:server',
  connectionKey: '/v1/connections/:server/key',
  connectionSignIn: '/v1/connections/:server/sign-in',
  signInStart: '/v1/sign-in/start',
  signInBegin: '/v1/sign-in/begin',
  signInCallback: '/v1/sign-in/callback',
  signInClient: '/v1/sign-in/clients/:tenantId',
  skills: '/v1/skills',
  skillMarketplace: '/v1/skills/marketplace',
  skill: '/v1/skills/:id',
} as const;

export const CONSOLE_ROUTES = {
  signInProviders: '/v1/console/auth/providers',
  signIn: (provider: string) => `/v1/console/auth/login/${provider}`,
  signInCallback: (provider: string) => `/v1/console/auth/callback/${provider}`,
  devSignIn: '/v1/console/auth/dev',
  signOut: '/v1/console/auth/logout',
  me: '/v1/console/me',
  inviteAccept: '/v1/console/invites/accept',
  team: (tenant: TenantId) => `/v1/console/tenants/${tenant}/team`,
  teamInvites: (tenant: TenantId) => `/v1/console/tenants/${tenant}/team/invites`,
  teamInvite: (tenant: TenantId, invite: string) =>
    `/v1/console/tenants/${tenant}/team/invites/${invite}`,
  teamMember: (tenant: TenantId, account: string) =>
    `/v1/console/tenants/${tenant}/team/members/${account}`,
  settings: (tenant: TenantId) => `/v1/console/tenants/${tenant}/settings`,
  integrations: (tenant: TenantId) => `/v1/console/tenants/${tenant}/integrations`,
  origins: (tenant: TenantId) => `/v1/console/tenants/${tenant}/integrations/origins`,
  mcpServers: (tenant: TenantId) => `/v1/console/tenants/${tenant}/integrations/mcp-servers`,
  mcpServer: (tenant: TenantId, name: string) =>
    `/v1/console/tenants/${tenant}/integrations/mcp-servers/${name}`,
  apiKeys: (tenant: TenantId) => `/v1/console/tenants/${tenant}/integrations/api-keys`,
  apiKey: (tenant: TenantId, key: string) =>
    `/v1/console/tenants/${tenant}/integrations/api-keys/${key}`,
  history: (tenant: TenantId) => `/v1/console/tenants/${tenant}/history`,
  tools: (tenant: TenantId) => `/v1/console/tenants/${tenant}/tools`,
  tool: (tenant: TenantId, source: string, tool: string) =>
    `/v1/console/tenants/${tenant}/tools/${encodeURIComponent(source)}/${encodeURIComponent(tool)}`,
  conversations: (tenant: TenantId) => `/v1/console/tenants/${tenant}/conversations`,
  conversationTrace: (tenant: TenantId, conversation: ConversationId, user: string) =>
    `/v1/console/tenants/${tenant}/conversations/${conversation}?user=${encodeURIComponent(user)}`,
  conversationEvalCase: (
    tenant: TenantId,
    conversation: ConversationId,
    message: MessageId,
    user: string,
  ) =>
    `/v1/console/tenants/${tenant}/conversations/${conversation}/messages/${message}/eval-case?user=${encodeURIComponent(user)}`,
  toolHealth: (tenant: TenantId) => `/v1/console/tenants/${tenant}/tool-health`,
  usage: (tenant: TenantId) => `/v1/console/tenants/${tenant}/usage`,
  creditLimits: (tenant: TenantId) => `/v1/console/tenants/${tenant}/credit-limits`,
  overview: (tenant: TenantId) => `/v1/console/tenants/${tenant}/overview`,
  evalCases: (tenant: TenantId) => `/v1/console/tenants/${tenant}/evals/cases`,
  evalCase: (tenant: TenantId, evalCase: string) =>
    `/v1/console/tenants/${tenant}/evals/cases/${evalCase}`,
  evalCaseArchive: (tenant: TenantId, evalCase: string) =>
    `/v1/console/tenants/${tenant}/evals/cases/${evalCase}/archive`,
  skills: (tenant: TenantId) => `/v1/console/tenants/${tenant}/skills`,
  skill: (tenant: TenantId, skill: string) => `/v1/console/tenants/${tenant}/skills/${skill}`,
  skillVersions: (tenant: TenantId, skill: string) =>
    `/v1/console/tenants/${tenant}/skills/${skill}/versions`,
  evalRuns: (tenant: TenantId) => `/v1/console/tenants/${tenant}/evals/runs`,
  evalRun: (tenant: TenantId, run: string) => `/v1/console/tenants/${tenant}/evals/runs/${run}`,
  testAccounts: (tenant: TenantId) => `/v1/console/tenants/${tenant}/test-accounts`,
  testAccount: (tenant: TenantId, name: string) =>
    `/v1/console/tenants/${tenant}/test-accounts/${name}`,
  previewSessions: (tenant: TenantId) => `/v1/console/tenants/${tenant}/preview/sessions`,
} as const;

export const CONSOLE_ROUTE_PATTERNS = {
  prefix: '/v1/console',
  signInProviders: '/v1/console/auth/providers',
  signIn: '/v1/console/auth/login/:provider',
  signInCallback: '/v1/console/auth/callback/:provider',
  devSignIn: '/v1/console/auth/dev',
  signOut: '/v1/console/auth/logout',
  me: '/v1/console/me',
  inviteAccept: '/v1/console/invites/accept',
  team: '/v1/console/tenants/:tenantId/team',
  teamInvites: '/v1/console/tenants/:tenantId/team/invites',
  teamInvite: '/v1/console/tenants/:tenantId/team/invites/:inviteId',
  teamMember: '/v1/console/tenants/:tenantId/team/members/:accountId',
  settings: '/v1/console/tenants/:tenantId/settings',
  integrations: '/v1/console/tenants/:tenantId/integrations',
  origins: '/v1/console/tenants/:tenantId/integrations/origins',
  mcpServers: '/v1/console/tenants/:tenantId/integrations/mcp-servers',
  mcpServer: '/v1/console/tenants/:tenantId/integrations/mcp-servers/:name',
  apiKeys: '/v1/console/tenants/:tenantId/integrations/api-keys',
  apiKey: '/v1/console/tenants/:tenantId/integrations/api-keys/:keyId',
  history: '/v1/console/tenants/:tenantId/history',
  tools: '/v1/console/tenants/:tenantId/tools',
  tool: '/v1/console/tenants/:tenantId/tools/:source/:tool',
  conversations: '/v1/console/tenants/:tenantId/conversations',
  conversationTrace: '/v1/console/tenants/:tenantId/conversations/:conversationId',
  conversationEvalCase:
    '/v1/console/tenants/:tenantId/conversations/:conversationId/messages/:messageId/eval-case',
  toolHealth: '/v1/console/tenants/:tenantId/tool-health',
  usage: '/v1/console/tenants/:tenantId/usage',
  creditLimits: '/v1/console/tenants/:tenantId/credit-limits',
  overview: '/v1/console/tenants/:tenantId/overview',
  evalCases: '/v1/console/tenants/:tenantId/evals/cases',
  evalCase: '/v1/console/tenants/:tenantId/evals/cases/:caseId',
  evalCaseArchive: '/v1/console/tenants/:tenantId/evals/cases/:caseId/archive',
  skills: '/v1/console/tenants/:tenantId/skills',
  skill: '/v1/console/tenants/:tenantId/skills/:skillId',
  skillVersions: '/v1/console/tenants/:tenantId/skills/:skillId/versions',
  evalRuns: '/v1/console/tenants/:tenantId/evals/runs',
  evalRun: '/v1/console/tenants/:tenantId/evals/runs/:runId',
  testAccounts: '/v1/console/tenants/:tenantId/test-accounts',
  testAccount: '/v1/console/tenants/:tenantId/test-accounts/:name',
  previewSessions: '/v1/console/tenants/:tenantId/preview/sessions',
} as const;
